import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { programsData } from '../../data/programsData';
import { Link } from 'react-router-dom';
import { FaFire, FaArrowRight } from 'react-icons/fa6';

const Programs = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Strength', 'Hypertrophy', 'Fat Loss', 'Beginner', 'Conditioning', 'Performance', '1-on-1 Coaching'];

  const filteredData = programsData.filter((p) => {
    if (selectedCategory === 'All') return true;
    return p.category.toLowerCase().includes(selectedCategory.toLowerCase());
  });

  const transition = { type: 'spring', duration: 2 };

  return (
    <section className="relative flex flex-col gap-8 md:gap-12 px-4 sm:px-6 md:px-12 py-10 md:py-20 max-w-7xl mx-auto overflow-hidden w-full" id="programs">
      {/* Background Ambient Glows */}
      <div className="absolute w-64 sm:w-96 h-64 sm:h-96 bg-red-600/15 blur-[150px] rounded-full top-0 left-1/4 pointer-events-none -z-10" />
      <div className="absolute w-64 sm:w-96 h-64 sm:h-96 bg-red-800/10 blur-[150px] rounded-full bottom-0 right-1/4 pointer-events-none -z-10" />

      {/* Header */}
      <motion.div
        className="flex flex-col items-center text-center gap-3 sm:gap-4 max-w-3xl mx-auto w-full px-2"
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={transition}
        viewport={{ once: true }}
      >
        <span className="inline-flex items-center gap-2 bg-red-600/15 border border-red-500/30 text-red-400 text-[10px] sm:text-xs font-black uppercase tracking-[0.18em] px-3.5 py-1.5 rounded-full shadow-lg">
          <FaFire className="text-red-500 text-xs" /> Training Built Around You
        </span>

        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none flex flex-wrap justify-center items-center gap-2 sm:gap-3 text-white">
          <span className="text-transparent [-webkit-text-stroke:_1px_white] sm:[-webkit-text-stroke:_1.5px_white]">EXPLORE</span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-red-400 to-red-600">
            YOUR GOAL
          </span>
        </h2>

        <p className="text-gray-300 text-xs sm:text-sm md:text-base max-w-xl leading-relaxed px-2">
          Select your training goal to view our 9 science-backed workout blueprints designed for every fitness stage.
        </p>

        {/* Responsive Category Filter Pills Bar */}
        <div className="w-full pt-1 sm:pt-2">
          <div className="flex items-center gap-2 overflow-x-auto py-2 px-1 max-w-full no-scrollbar sm:justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3.5 sm:px-4 py-2 rounded-xl text-[10px] sm:text-xs font-extrabold uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer border flex-shrink-0 ${
                  selectedCategory === cat
                    ? 'bg-red-600 text-white border-red-500 shadow-lg shadow-red-600/40 scale-105'
                    : 'bg-[#14161f]/90 text-gray-300 border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Program Cards Grid - Fully Responsive */}
      <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8 items-stretch w-full">
        <AnimatePresence>
          {filteredData.map((program, index) => (
            <motion.div
              layout
              key={program.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              whileHover={{ y: -6 }}
              className="flex flex-col bg-gradient-to-b from-[#181a24]/95 via-[#13151f]/95 to-[#0d0e14]/95 border border-white/10 hover:border-red-500/60 p-5 sm:p-7 gap-5 text-white justify-between rounded-3xl transition-all duration-300 shadow-2xl group relative overflow-hidden h-full"
            >
              {/* Top Accent Strip */}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-red-600 to-transparent group-hover:via-red-400 transition-all" />

              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-red-600/25 to-red-900/25 border border-red-500/30 flex items-center justify-center text-red-500 group-hover:bg-red-600 group-hover:text-white transition-all shadow-md [&>svg]:w-5 [&>svg]:h-5 sm:[&>svg]:w-6 sm:[&>svg]:h-6 flex-shrink-0">
                    {program.icon}
                  </div>
                  <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-red-400 bg-red-600/15 px-2.5 sm:px-3 py-1 rounded-full border border-red-500/30">
                    0{index + 1} • {program.category}
                  </span>
                </div>

                <h3 className="text-base sm:text-xl font-black uppercase text-white group-hover:text-red-400 transition-colors leading-tight">
                  {program.heading}
                </h3>

                <div className="flex flex-col gap-3 bg-[#0c0d12]/80 p-3.5 sm:p-4 rounded-2xl border border-white/5 text-xs text-gray-300 leading-relaxed">
                  <div>
                    <strong className="text-red-400 font-black uppercase text-[10px] sm:text-[11px] tracking-wider block mb-1">Ideal For:</strong>
                    <p className="text-gray-200 text-xs sm:text-[13px] leading-relaxed">{program.idealFor}</p>
                  </div>
                  <div className="pt-2.5 border-t border-white/10">
                    <strong className="text-red-500 font-black uppercase text-[10px] sm:text-[11px] tracking-wider block mb-1">Explore Methodology:</strong>
                    <p className="text-gray-300 text-xs sm:text-[13px] leading-relaxed">{program.explore}</p>
                  </div>
                </div>
              </div>

              <Link
                to={`/explore#${program.id}`}
                state={{ programId: program.id }}
                className="flex items-center justify-between pt-4 font-black text-xs uppercase tracking-wider text-red-400 group-hover:text-white transition-colors border-t border-white/10 mt-2"
              >
                <span>Explore Full Blueprint</span>
                <FaArrowRight className="text-xs group-hover:translate-x-1.5 transition-transform text-red-500 group-hover:text-white" />
              </Link>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Bottom CTA Banner */}
      <div className="bg-gradient-to-r from-red-600/20 via-[#141620] to-red-600/20 border border-red-500/30 p-5 sm:p-8 rounded-3xl flex flex-col md:flex-row items-center justify-between gap-4 backdrop-blur-xl shadow-2xl text-center md:text-left">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-black uppercase tracking-widest text-red-400">Can't decide on a program?</span>
          <p className="text-xs sm:text-base font-bold text-white">Get a free fitness assessment & custom recommendation from an IDOL trainer.</p>
        </div>
        <Link
          to="/explore"
          className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white text-xs font-black uppercase tracking-wider px-6 py-3.5 rounded-xl shadow-lg shadow-red-600/30 transition-all hover:scale-105 whitespace-nowrap flex-shrink-0"
        >
          View All 9 Blueprints ↗
        </Link>
      </div>
    </section>
  );
};

export default Programs;